import { Mail, Visibility, Delete, Edit } from "@mui/icons-material";
import LSModule from "../../components/base/LSModule";
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Fab, Grid, TextField, IconButton as MuiIconButton, Typography, CircularProgress } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { usePermissions } from '../../contexts/PermissionContext';
import { PERMISSIONS } from '../../constants/permissions';
import { LysepAPI } from '../../contexts/ApiContext';
import { Badge } from '../../components/ui/Badge';
import { IconButton as UiIconButton } from '../../components/ui/Button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/Table";
import { Pagination } from "../../components/ui/Pagination";

// HOC to inject permissions into class component
const withPermissions = (Component) => {
    return (props) => {
        const permissions = usePermissions();
        return <Component {...props} permissions={permissions} />;
    };
};

// Contact detay dialog'u
function ContactDetailDialog(props) {
    const { contact, open, onClose } = props;

    if (!contact) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Mail color="primary" />
                    <Typography variant="h6">Contact Message</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="First Name"
                            value={contact.firstName || ''}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Last Name"
                            value={contact.lastName || ''}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Email"
                            value={contact.email || ''}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Phone"
                            value={contact.phoneNumber || 'N/A'}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Company"
                            value={contact.companyName || 'N/A'}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Message"
                            value={contact.message || ''}
                            multiline
                            rows={6}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Status"
                            value={contact.status || 'new'}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Read Status"
                            value={contact.isRead ? 'Read' : 'Unread'}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Submitted At"
                            value={new Date(contact.created_at).toLocaleString()}
                            fullWidth
                            disabled
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

class ContactsModule extends LSModule {
    state = {
        selectedContact: null,
        isDetailOpen: false,
        stats: null,
        // DataGrid replacement state
        messages: [],
        loading: false,
        page: 1,
        limit: 10,
        totalHelpers: 0,
        totalPages: 1
    }

    componentDidMount() {
        this.fetchStats();
        this.fetchData();
    }

    fetchStats = async () => {
        try {
            const response = await LysepAPI.get('/api/contact/stats');
            if (response.data && response.data.success) {
                this.setState({ stats: response.data.data });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }

    fetchData = async () => {
        this.setState({ loading: true });
        try {
            const { page, limit } = this.state;
            const offset = (page - 1) * limit;

            const response = await LysepAPI.get('/api/contact/messages', {
                params: { limit, offset }
            });

            if (response.data && response.data.success && response.data.data && response.data.data.messages) {
                const messages = response.data.data.messages;
                const total = response.data.data.pagination ? response.data.data.pagination.total : messages.length;

                this.setState({
                    messages: messages,
                    totalHelpers: total,
                    totalPages: Math.ceil(total / limit),
                    loading: false
                });
            } else {
                this.setState({ loading: false });
                toast.error("Mesajlar yüklenemedi.");
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            this.setState({ loading: false });
            toast.error("Veri çekme hatası.");
        }
    }

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, () => {
            this.fetchData();
        });
    }

    handleViewContact = (contact) => {
        this.setState({
            selectedContact: contact,
            isDetailOpen: true
        });
    }

    handleCloseDetail = () => {
        this.setState({
            selectedContact: null,
            isDetailOpen: false
        });
    }

    handleStatusChange = async (contactId, newStatus) => {
        try {
            const response = await LysepAPI.put(`/api/contact/messages/${contactId}/status`, {
                status: newStatus
            });

            if (response.data && response.data.success) {
                toast.success('Status updated successfully!');
                this.fetchData();
                this.fetchStats(); // Stats'ı da yenile
            } else {
                toast.error('Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Error updating status');
        }
    }

    handleMarkAsRead = async (contactId) => {
        try {
            const response = await LysepAPI.put(`/api/contact/messages/${contactId}/status`, {
                isRead: true
            });

            if (response.data && response.data.success) {
                toast.success('Marked as read!');
                this.fetchData();
                this.fetchStats(); // Stats'ı da yenile
            } else {
                toast.error('Failed to mark as read');
            }
        } catch (error) {
            console.error('Error marking as read:', error);
            toast.error('Error marking as read');
        }
    }

    handleDeleteContact = async (contactId) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) {
            return;
        }

        try {
            const response = await LysepAPI.delete(`/api/contact/messages/${contactId}`);

            if (response.data && response.data.success) {
                toast.success('Contact deleted successfully!');
                this.fetchData();
                this.fetchStats();
            } else {
                toast.error('Failed to delete contact');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            toast.error('Error deleting contact');
        }
    }

    render() {
        const { hasPermission } = this.props.permissions || {};
        const { stats, messages, loading, page, totalPages } = this.state;

        return (
            <Box sx={{ padding: '1em' }}>
                {/* Stats Cards */}
                {stats && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.total}</Typography>
                                <Typography variant="body2">Total Messages</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'error.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.unread}</Typography>
                                <Typography variant="body2">Unread</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'warning.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.new}</Typography>
                                <Typography variant="body2">New</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'info.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.inProgress}</Typography>
                                <Typography variant="body2">In Progress</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'success.main', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.completed}</Typography>
                                <Typography variant="body2">Completed</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ p: 2, bgcolor: 'grey.500', color: 'white', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h4">{stats.last30Days}</Typography>
                                <Typography variant="body2">Last 30 Days</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {/* Table Replacement */}
                <div className="space-y-4">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[60px]">ID</TableHead>
                                <TableHead>First Name</TableHead>
                                <TableHead>Last Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Company</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Read</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="h-24 text-center">
                                        <CircularProgress size={20} />
                                    </TableCell>
                                </TableRow>
                            ) : messages.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="h-24 text-center text-gray-400">
                                        Mesaj bulunamadı.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                messages.map((contact) => (
                                    <TableRow key={contact.id}>
                                        <TableCell>{contact.id}</TableCell>
                                        <TableCell>{contact.firstName}</TableCell>
                                        <TableCell>{contact.lastName}</TableCell>
                                        <TableCell>{contact.email}</TableCell>
                                        <TableCell>{contact.companyName}</TableCell>
                                        <TableCell>
                                            <Badge variant={
                                                {
                                                    'new': 'destructive',
                                                    'in_progress': 'warning',
                                                    'completed': 'success',
                                                    'archived': 'default'
                                                }[contact.status || 'new'] || 'default'
                                            }>
                                                {(contact.status || 'new').toUpperCase()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={contact.isRead ? 'success' : 'destructive'}>
                                                {contact.isRead ? 'READ' : 'UNREAD'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(contact.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <UiIconButton
                                                    size="iconSm"
                                                    icon={Visibility}
                                                    onClick={() => this.handleViewContact(contact)}
                                                    variant="ghost"
                                                />
                                                {hasPermission && hasPermission(PERMISSIONS.CONTACT_MANAGEMENT) && (
                                                    <>
                                                        <UiIconButton
                                                            size="iconSm"
                                                            icon={Edit}
                                                            onClick={() => this.handleStatusChange(contact.id, 'in_progress')}
                                                            variant="warning"
                                                        />
                                                        <UiIconButton
                                                            size="iconSm"
                                                            icon={Delete}
                                                            onClick={() => this.handleDeleteContact(contact.id)}
                                                            variant="destructive"
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={this.handlePageChange}
                    />
                </div>

                {/* Contact Detail Dialog */}
                <ContactDetailDialog
                    contact={this.state.selectedContact}
                    open={this.state.isDetailOpen}
                    onClose={this.handleCloseDetail}
                />
            </Box>
        );
    }
}

export default withPermissions(ContactsModule);
